"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Code2,
  Loader2,
  Mail,
  Phone,
  PhoneOutgoing,
  Wallet,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { faDateTime } from "@/lib/format";
import type { ProjectRequest, ProjectRequestStatus } from "./types";

type Filter = "ALL" | ProjectRequestStatus;

function StatusBadge({ status }: { status: ProjectRequestStatus }) {
  if (status === "DONE")
    return (
      <Badge className="bg-primary/15 text-primary hover:bg-primary/20">
        <CheckCircle2 className="size-3" />
        انجام شده
      </Badge>
    );
  if (status === "CONTACTED")
    return (
      <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400">
        در حال پیگیری
      </Badge>
    );
  return (
    <Badge className="bg-rose-500/15 text-rose-600 hover:bg-rose-500/20 dark:text-rose-400">
      جدید
    </Badge>
  );
}

export default function ProjectRequestsPanel() {
  const [requests, setRequests] = React.useState<ProjectRequest[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<Filter>("ALL");
  const [updatingId, setUpdatingId] = React.useState<string | null>(null);

  const loadRequests = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/project-requests", {
        cache: "no-store",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در دریافت درخواست‌ها");
      }
      setRequests(data.requests ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت درخواست‌ها";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const filtered = React.useMemo(() => {
    return requests.filter(
      (r) => filter === "ALL" || r.status === filter,
    );
  }, [requests, filter]);

  async function updateStatus(r: ProjectRequest, status: ProjectRequestStatus) {
    setUpdatingId(r.id);
    try {
      const res = await fetch("/api/admin/project-requests", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: r.id, status }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در به‌روزرسانی درخواست");
      }
      toast.success(
        status === "CONTACTED" ? "به‌عنوان «در حال پیگیری» ثبت شد." : "به‌عنوان «انجام شده» ثبت شد.",
      );
      loadRequests();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در به‌روزرسانی درخواست";
      toast.error(msg);
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="border-b">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Code2 className="size-5 text-primary" />
              درخواست‌های پروژه
            </CardTitle>
            <Select
              value={filter}
              onValueChange={(v) => setFilter(v as Filter)}
            >
              <SelectTrigger className="h-9 w-40 text-sm">
                <SelectValue placeholder="همه" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">همه</SelectItem>
                <SelectItem value="NEW">جدید</SelectItem>
                <SelectItem value="CONTACTED">در حال پیگیری</SelectItem>
                <SelectItem value="DONE">انجام شده</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loading ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-44 w-full rounded-2xl" />
              ))}
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-destructive">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              درخواستی یافت نشد.
            </div>
          ) : (
            <div className="max-h-[32rem] overflow-y-auto pr-1 scrollbar-thin">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {filtered.map((r) => (
                  <Card
                    key={r.id}
                    className="overflow-hidden rounded-2xl border-border/60 shadow-sm"
                  >
                    <CardHeader className="border-b bg-muted/30 py-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="flex size-10 items-center justify-center rounded-full bg-primary/15 text-primary">
                            <Code2 className="size-5" />
                          </span>
                          <div>
                            <div className="font-medium">{r.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {faDateTime(r.createdAt)}
                            </div>
                          </div>
                        </div>
                        <StatusBadge status={r.status} />
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3 pt-4">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="size-4 shrink-0 text-muted-foreground" />
                          <span dir="ltr" className="text-right font-medium">
                            {r.phone}
                          </span>
                        </div>
                        {r.email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="size-4 shrink-0 text-muted-foreground" />
                            <span dir="ltr" className="text-right">
                              {r.email}
                            </span>
                          </div>
                        )}
                        {r.projectType && (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs text-muted-foreground">
                              نوع پروژه
                            </span>
                            <span className="text-sm font-medium">
                              {r.projectType}
                            </span>
                          </div>
                        )}
                        {r.budget && (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs text-muted-foreground">
                              بودجه
                            </span>
                            <span className="text-sm font-medium">{r.budget}</span>
                          </div>
                        )}
                      </div>
                      <div className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
                        {r.description}
                      </div>
                      {r.status !== "DONE" && (
                        <div className="flex flex-wrap justify-end gap-2">
                          {r.status === "NEW" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={updatingId === r.id}
                              onClick={() => updateStatus(r, "CONTACTED")}
                            >
                              {updatingId === r.id ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : (
                                <PhoneOutgoing className="size-4" />
                              )}
                              تماس گرفتم
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="bg-primary hover:bg-primary/90"
                            disabled={updatingId === r.id}
                            onClick={() => updateStatus(r, "DONE")}
                          >
                            <CheckCircle2 className="size-4" />
                            انجام شد
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 rounded-xl border bg-muted/40 p-3 text-xs text-muted-foreground">
        <Wallet className="size-4 text-primary" />
        درخواست‌ها از فرم «سفارش پروژه» در سایت ثبت می‌شوند.
      </div>
    </div>
  );
}
